import React, {useEffect} from 'react';
import {FlatList, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react';

import LoadingImg from '../../components/LoadingImg';
import StoreItem from '../../components/StoreItem';
import Button from '../../components/ui-controls/Button';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {Shop} from '../../models/middleware';
import {RootStoreSelectionNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventScreenName} from '../../utils/analytics/events';

const StoreSelectionScreen = ({
  navigation,
}: RootStoreSelectionNavigatorScreenProps<AppRoute.MY_STORE_STORE_SELECTION>) => {
  const {shopSelectionStore} = useStores();

  useEffect(() => {
    if (shopSelectionStore.suburbList.length === 0) {
      navigation.navigate(AppRoute.MY_STORE_SUBURB_SELECTION);
    }
  }, [navigation, shopSelectionStore]);

  const selectStore = (store: Shop) => {
    shopSelectionStore.selectStore(store);
    shopSelectionStore.setCanDeliver(store.delivery_available);
    shopSelectionStore.setCanPickup(store.pickup_available);

    Analytics.logEvent({
      eventName: EventName.StoreSelected,
      parameters: {
        screen_name: EventScreenName.StoreSelection,
        store_id: store.store_number,
        store_name: store.name,
        store_postcode: store.address?.postal_code,
        store_distance: store.distance,
        store_delivery_available: store.delivery_available,
        store_pickup_available: store.pickup_available,
      },
    });
  };

  const expandSearch = () => {
    shopSelectionStore.expandSearchRange();
    shopSelectionStore.resetLoading();
    shopSelectionStore.selectSuburb();
  };

  const handleChangeLocationPress = () =>
    navigation.navigate(AppRoute.MY_STORE_SUBURB_SELECTION);

  const separator = () => {
    return (
      <View
        style={{
          marginVertical: 4,
          borderBottomWidth: 1,
          borderBottomColor: 'lightgrey',
        }}
      />
    );
  };

  const date = new Date();
  const dayName = date.toLocaleString('au-AU', {weekday: 'long'}).toLowerCase();

  const ChangeLocationButton = () => {
    const suburbName = shopSelectionStore.selectedSuburb?.locality;
    return (
      <View>
        <Button onPress={handleChangeLocationPress} title="CHANGE LOCATION" />
        <Text style={styles.changeLocationCopy}>
          Delivery options are shown for {suburbName}.
        </Text>
      </View>
    );
  };

  const storeList = () => {
    if (!shopSelectionStore.storeList.length) {
      return (
        <View style={styles.noStoresSection}>
          <Text>
            No stores were found within {shopSelectionStore.maxDistance} km of
            location.
          </Text>

          <View style={styles.searchAgainSection}>
            <Button
              onPress={expandSearch}
              title={`Search again up to ${
                shopSelectionStore.maxDistance + 200
              } km`}
            />
          </View>
        </View>
      );
    }
    return (
      <FlatList
        data={shopSelectionStore.storeList}
        renderItem={({item}) => (
          <StoreItem item={item} dayName={dayName} selectStore={selectStore} />
        )}
        ItemSeparatorComponent={separator}
        keyExtractor={item => item.store_number}
        fadingEdgeLength={100}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={ChangeLocationButton}
        ListHeaderComponentStyle={{marginBottom: 16}}
      />
    );
  };

  if (shopSelectionStore.isStoreListLoading()) {
    return (
      <View style={styles.primaryWaitingContainer}>
        <LoadingImg />
      </View>
    );
  }

  return (
    <ContentView>
      <View style={styles.container}>{storeList()}</View>
    </ContentView>
  );
};

const styles = StyleSheet.create({
  primaryWaitingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colour.background.general.primary,
  },
  container: {
    flex: 1,
  },
  noStoresSection: {
    marginTop: 30,
    textAlign: 'center',
  },
  searchAgainSection: {
    marginTop: 14,
    display: 'flex',
    justifyContent: 'center',
  },
  changeLocationCopy: {
    paddingTop: 8,
  },
});

export default observer(StoreSelectionScreen);
